<template>
    <div class="h-full p-4">
        <div
            v-if="isQueueActivityGauge(data)"
            class="w-full h-full"
        >
            <redesign-queue-activity
                :widget="data"
                :metrics="metrics"
            />
        </div>
        <div
            v-else
            class="h-full"
        >
            <div
                v-if="visibleMetrics?.length"
                :class="[
                    'flex flex-wrap gap-4',
                    stretchToWidgetSize ? 'h-full justify-evenly items-stretch' : 'justify-center'
                ]"
            >
                <div
                    v-for="metric in visibleMetrics"
                    :key="metric.key"
                    :class="[
                        'relative group',
                        stretchToWidgetSize ? 'flex-1 min-w-[200px]' : ''
                    ]"
                >
                    <div
                        v-show="metric.key !== 'other'"
                        class="
                    absolute
                    top-2
                    right-2
                    z-10
                    opacity-0
                    group-hover:opacity-100
                    transition-opacity
                    cursor-pointer
                    bg-white
                    rounded
                    shadow
                    p-1
                "
                        @click.stop="openMetricConfig(metric.key)"
                    >
                        <RedesignBaseIcon
                            name="Edit"
                            :size="16"
                            class="text-secondary-actions hover:text-primary-actions"
                        />
                    </div>
                    <redesign-statistic-card
                        :label="metric.label"
                        :value="metric.value"
                        :absolute-value="metric.count"
                        :type="metric.type"
                        :value-font-size="valueFontSize"
                        :label-font-size="labelFontSize"
                        :show-absolute-value="showAbsoluteNumbers"
                        :color="metric.color"
                        :card-config="cardsConfig[metric.key]"
                        :stretch-to-widget-size="stretchToWidgetSize"
                        :text-align="cardTextAlign"
                    />
                </div>
            </div>
            <QueueDashboardConfigModal
                v-if="openedMetricKey"
                :visible="true"
                :metric-key="openedMetricKey"
                :type="ALL_METRICS[openedMetricKey].type"
                :initial-config="cardsConfig[openedMetricKey]"
                @save="onSaveMetricConfig"
                @close="openedMetricKey = null"
            />
        </div>
    </div>
</template>

<script setup lang="ts">

import { QueueDashboardCardConfig, WidgetExtended } from '@/types/dashboard'
import useWidgetDataWithRefreshInterval from '@/composables/useWidgetDataWithRefreshInterval'
import { computed, ref } from 'vue'
import {
    ADDITIONAL_DATA_KEY,
    buildDefaultQueueDashboardConfig,
    COUNTERS_TYPE,
    getAllMetrics,
    MetricsState,
    PERCENTAGE_TYPE,
    TIME_TYPE
} from '@/enum/redesignQueueDashboardStatistics'
import {
    billingCdrQueueTypes,
    billingCdrQueueTypesReversed,
    multiQueueColumnsPropNames,
    QueueData
} from '@/helpers/multiQueueDashboardRedesign'
import RedesignStatisticCard from '@/components/RedesignComponents/Cards/RedesignStatisticCard.vue'
import QueueDashboardConfigModal from '@/components/RedesignComponents/Modal/QueueDashboardConfigModal.vue'
import RedesignBaseIcon from '@/components/RedesignBaseComponents/RedesignBaseIcon.vue'
import { isQueueActivityGauge } from '@/helpers/widgetUtils'
import RedesignQueueActivity from '@/components/RedesignComponents/Widgets/RedesignQueueActivity.vue'
import { useI18n } from 'vue-i18n'
import cloneDeep from 'lodash/cloneDeep'
import { timeFormatter } from '@/helpers/timeFormatter'

const { t } = useI18n()

const emit = defineEmits<{
    (event: 'on-update', widgetData: WidgetExtended): void
}>()

const props = defineProps<{
    data: WidgetExtended
}>()


const { data: queueData } = useWidgetDataWithRefreshInterval<QueueData, {data: WidgetExtended}, 'data'>(props, 'data')

//Computed
const ALL_METRICS = computed(() => getAllMetrics())
const showSumOfOthers = computed(() => props.data?.WidgetLayout?.SumOfOthers)

const showAbsoluteNumbers = computed(() => props.data?.WidgetLayout?.AbsoluteNumbers ?? false)

const stretchToWidgetSize = computed(() => props.data?.WidgetLayout?.stretchToWidgetSize ?? false)

const cardTextAlign = computed(() => props.data?.WidgetLayout?.cardTextAlign ?? 'center')

const visibleMetricsSet = computed(() => new Set(props.data?.WidgetLayout?.ShowStatistics || []))

const valueFontSize = computed(() => {
    return props.data?.WidgetLayout?.valueFontSize ?? 36
})

const labelFontSize = computed(() => {
    return props.data?.WidgetLayout?.titleFontSize ?? 24
})

const defaultQueueDashboardConfig = computed(() => {
    return buildDefaultQueueDashboardConfig()
})

const cardsConfig = computed(() => {
    return {
        ...defaultQueueDashboardConfig.value,
        ...(props.data.WidgetLayout.queueDashboardConfig ?? {})
    }
})

// Base metrics state without label - labels come from ALL_METRICS which are translated at runtime
function createBaseMetricsState (): MetricsState {
    const state: MetricsState = {}

    for (const key in ALL_METRICS.value) {
        state[key] = {
            count: 0,
            value: 0,
            label: ALL_METRICS.value[key].label, // Use translated label from ALL_METRICS
            key,
            type: ALL_METRICS.value[key].type,
            color: ALL_METRICS.value[key].color
        }
    }
    return state
}

const openedMetricKey = ref<string | null>(null)

function applyExitCounts (queue: QueueData, state: MetricsState) {
    if (!Array.isArray(queue?.[ADDITIONAL_DATA_KEY])) return

    queue.ExitCounts.forEach(exit => {
        const metricKey = billingCdrQueueTypes[exit.billing_cdr_queue_type]
        if (!metricKey) return

        const count = exit.ExitTypeCount ?? 0
        state[metricKey].count += count
    })
}

const metrics = computed(() => {
    const baseState = createBaseMetricsState()

    if (!queueData.value || !Array.isArray(queueData.value)) {
        return structuredClone(baseState)
    }

    const temp = structuredClone(baseState)

    let maxRing = 0
    let avgWeightedSum = 0

    queueData.value.forEach(queue => {
        for (const key in ALL_METRICS.value) {
            const value = queue[key]

            switch (key) {
                case multiQueueColumnsPropNames.MAX_RING_TIME:
                    if (value && value > maxRing) {
                        maxRing = value
                    }
                    break

                case multiQueueColumnsPropNames.AVG_RING_TIME:
                    if (value) {
                        const calls = queue[multiQueueColumnsPropNames.CALL_COUNT] ?? 0
                        avgWeightedSum += value * calls
                    }
                    break

                default:
                    temp[key].count += value ?? 0
                    break
            }
        }
        applyExitCounts(queue, temp)
    })


    temp[multiQueueColumnsPropNames.MAX_RING_TIME].count = maxRing

    const totalCalls = temp[multiQueueColumnsPropNames.CALL_COUNT].count
    temp[multiQueueColumnsPropNames.AVG_RING_TIME].count = totalCalls > 0 ? avgWeightedSum / totalCalls : 0

    const totalAnswered = temp[multiQueueColumnsPropNames.ANSWER].count
    let hoursElapsed = 0

    if (queueData.value.length > 0 && queueData.value[0].HoursElapsed) {
        hoursElapsed = queueData.value[0].HoursElapsed
    }

    temp[multiQueueColumnsPropNames.AVG_ANSWER_HOURLY].count =
        hoursElapsed > 0 ? parseFloat((totalAnswered / hoursElapsed).toFixed(2)) : 0

    temp[multiQueueColumnsPropNames.AVG_CALLS_HOURLY].count =
        hoursElapsed > 0 ? parseFloat((totalCalls / hoursElapsed).toFixed(2)) : 0

    return computePercentages(temp)
})

const otherMetric = computed(() => {
    if (!showSumOfOthers.value) return null
    const totalCalls = metrics.value[multiQueueColumnsPropNames.CALL_COUNT]?.count ?? 0
    if (!totalCalls) return null

    let hiddenCount = 0

    for (const key in billingCdrQueueTypesReversed) {
        if (visibleMetricsSet.value.has(key) || visibleMetricsSet.value.has(String(billingCdrQueueTypesReversed[key]))) continue
        hiddenCount += metrics.value[key]?.count ?? 0
    }

    if (!hiddenCount) return null

    return {
        key: 'other',
        label: t('widget.percentageOfOther'),
        count: hiddenCount,
        value: (hiddenCount * 100 / totalCalls).toFixed(2),
        type: PERCENTAGE_TYPE,
        color: 'var(--primary-color)',
        order: 25
    }
})


const visibleMetrics = computed(() => {
    const finalData = []
    for (const key in ALL_METRICS.value) {
        if (visibleMetricsSet.value.size && !(visibleMetricsSet.value.has(key) || visibleMetricsSet.value.has(String(billingCdrQueueTypesReversed[key]))) ) {
            continue
        }
        finalData.push({
            ...ALL_METRICS.value[key],
            ...metrics.value[key]
        })
    }
    if (showSumOfOthers.value && otherMetric.value) {
        finalData.push(otherMetric.value)
    }

    return finalData.sort((a, b) => cardsConfig.value[a.key]?.order - cardsConfig.value[b.key]?.order)
})

function openMetricConfig (key: string) {
    openedMetricKey.value = key
}

function computePercentages (state: MetricsState): MetricsState {
    const totalCalls =
        Number(state[multiQueueColumnsPropNames.CALL_COUNT]?.count) || 0

    for (const key in ALL_METRICS.value) {
        const metric = ALL_METRICS.value[key]
        const entry = state[key]

        if (!entry) continue

        switch (metric.type) {
            case PERCENTAGE_TYPE: {
                let count = Number(entry?.count) || 0

                // Special handling for AnsweredWithCBPercent: (Answer + TotalAnsweredCallbacks) / CallCount * 100
                if (key === multiQueueColumnsPropNames.ANSWERED_WITH_CB_PERCENT) {
                    const answerCount = Number(state[multiQueueColumnsPropNames.ANSWER]?.count) || 0
                    const totalAnsweredCallbacks = Number(state[multiQueueColumnsPropNames.TOTAL_ANSWERED_CALLBACKS]?.count) || 0
                    count = answerCount + totalAnsweredCallbacks
                    entry.count = count
                }

                entry.value = totalCalls > 0
                    ? ((count / totalCalls) * 100).toFixed(2)
                    : '0.00'
                break
            }

            case TIME_TYPE: {
                entry.value = timeFormatter(entry?.count ?? 0)
                break
            }

            case COUNTERS_TYPE: {
                entry.value = entry?.count ?? 0
                break
            }

            default:
                break
        }
    }
    return state
}


function onSaveMetricConfig (payload: {
    key: string
    config: QueueDashboardCardConfig
}) {
    const widgetData = cloneDeep(props.data)
    widgetData.WidgetLayout.queueDashboardConfig = {
        ...cardsConfig.value,
        [payload.key]: payload.config
    }
    updateWidget(widgetData)
}

const updateWidget = (widgetData: WidgetExtended) => {
    emit('on-update', widgetData)
}

</script>


